/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnDestroy, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, BehaviorSubject, EMPTY, Observable, MonoTypeOperatorFunction } from 'rxjs';
import {
	takeUntil,
	map,
	switchMap,
	filter,
	debounceTime,
	share,
	withLatestFrom,
	startWith,
	pairwise,
	tap,
} from 'rxjs/operators';
import { ticketListRace } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { Rectangle } from 'pixi.js-legacy';
import { calcBoundingRectFromTickets } from 'utils/ticket-utils';
import { PlanState } from 'src/app/services/plan-state.service';
import { ValidatedPlanActions } from 'src/app/services/validated-plan-actions.service';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { Ticket } from '@touchplan/common';
import { DraggableContainerComponent } from '../../../common/utility-components/draggable-container/draggable-container.component';
import { TicketActionTypes } from 'src/app/app-data/actions/ticket-actions';
import * as strings from '@touchplan/strings';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { clipboardManager } from 'src/app/plain-javascript/services/ClipboardManager';

import { PositionManagerService, WindowType } from 'src/app/services/position-manager.service';
import { ContainerType } from 'src/app/common/utility-components/base-container/ContainerLayerManager';
import { MatDialog } from '@angular/material/dialog';
import { TicketEditLocationsComponent } from '../ticket-edit-locations/ticket-edit-locations.component';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';

@Component({
	selector: 'multi-select-menu',
	templateUrl: './multi-select-menu.component.html',
	styleUrls: ['./multi-select-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectMenuComponent implements OnDestroy {
	@ViewChild('dragContainer', { static: false }) public dragContainer: DraggableContainerComponent;

	public anyCompleted$ = new BehaviorSubject(false);
	public showMenu$ = new BehaviorSubject(false);
	public hideMenu$ = new BehaviorSubject(false); // temp hide the menu without removing from dom
	private shutdownSubject$ = new Subject<void>();

	public isReadOnly$ = this.planAccessCheck.hasRestrictedAccess$;
	public AppStrings = strings;
	public showOptions = true;
	public enableClipboard$: Observable<boolean>;

	public tickets: Map<string, Ticket>;
	public numTickets: number;

	public ticket$ = new BehaviorSubject([]);

	public selectedTicketList = new BehaviorSubject<TicketList>(null);
	public isFloat$ = this.selectedTicketList.pipe(map((list) => list === this.planState.floatingTickets));
	public selectedActiveAndPullTickets$ = new BehaviorSubject(false);
	public containerType = ContainerType.MultiSelectMenu;

	//these are being repurposed now to mean "previous position before opening edit" since there are multiple observables and many emissions
	private previousX: number;
	private previousY: number;

	private readonly GAP_BETWEEN_TICKET_EDIT: number = 10;

	constructor(
		public changeDetectorRef: ChangeDetectorRef,
		private planState: PlanState,
		public planAccessCheck: PlanAccessCheck,
		private validatedPlanActions: ValidatedPlanActions,
		private ticketEditService: TicketEditService,
		private positionManagerService: PositionManagerService,
		private featureFlagManager: FeatureFlagManager,
		private dialog: MatDialog
	) {
		this.enableClipboard$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.CLIPBOARD);

		const selection$ = this.planState.stateChange$.pipe(
			switchMap((state) => {
				if (state <= 0) {
					return EMPTY;
				}

				//this technically supports floating ticket now, but floating tickets doesn't use this selected list
				// when implementing that, add a tap that ties the selection list back to the source TicketList for
				// the ticket based functions above
				return ticketListRace(
					(item) => item.list,
					this.planState.tickets.selectedTicket$,
					this.planState.floatingTickets.selectedTicket$
				);
			}),
			share() // turning this into hot/multicast obs to improve the performance due to all the pipes we have below
		);

		//figure out which parent TicketList is being operated on
		selection$
			.pipe(
				map((selectionList) => {
					if (selectionList === this.planState.floatingTickets.selectedTicket$.getValue()) {
						return this.planState.floatingTickets;
					}
					return this.planState.tickets;
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.selectedTicketList);

		//don't like this, but don't really want to rewrite the rest
		selection$.pipe(takeUntil(this.shutdownSubject$)).subscribe((selection) => {
			this.tickets = selection.list;
			this.numTickets = selection.list.size;
		});

		selection$
			.pipe(
				// convert to Array so Angular IterableDiffers works in cycle-tickets.component with ChangeDetectionStrategy.OnPush
				map((selection) => Array.from(selection.list.values())),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.ticket$);

		selection$
			.pipe(
				map((selection) => selection.list.size > 1),
				debounceTime(100),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((show: boolean) => {
				this.showMenu$.next(show);

				if (show) {
					this.showOptions = show;
					this.changeDetectorRef.detectChanges();
					this.positionMenu();
				}
			});

		selection$
			.pipe(
				map((selection) => selection.statistics.completed.size > 0),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.anyCompleted$);

		selection$
			.pipe(
				map((selection) => {
					return clipboardManager.hasActiveAndPullTickets(selection.list);
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.selectedActiveAndPullTickets$);

		// expand/collapse menu & TP-628: hide cycle ticket in multi-edit mode
		this.ticketEditService.combinedEdit$
			.pipe(
				debounceTime(100),
				this.stashBeforeEditPositionSideEffect((opened) => (opened ? this.updatePrev() : null)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((etc: EditTicketsCombined) => {
				const showFullMenu = this.showFullMenu(etc);
				if (this.showOptions === showFullMenu || !this.dragContainer) {
					return;
				}

				if (etc) {
					// etc
					const multiEditMode = etc.originalList.size > 1; // multiple tickets selected
					this.hideMenu$.next(multiEditMode);

					if (multiEditMode) {
						return; // skip the cycle ticket animation in multi edit mode
					}
				}

				// animate menu only when editing single ticket
				if (showFullMenu) {
					this.dragContainer.setX(this.previousX);
					this.dragContainer.setY(this.previousY);
				} else {
					// setTimeout because the container height changes, defer render so base-container can correctly calculate the bounds
					setTimeout(() => {
						// multi edit may have closed by the time this goes off
						if (!this.dragContainer) {
							return;
						}
						const position: Rectangle = this.positionManagerService.getPosition(WindowType.TicketEdit);
						// to align with ticket edit
						this.dragContainer.setX(position.x + position.width + this.GAP_BETWEEN_TICKET_EDIT);
						this.dragContainer.setY(position.y);
					}, 100);
				}

				this.showOptions = showFullMenu;
			});

		// Also subscribe to the PositionManagerService and update the Cycle Tickets position
		// when the Ticket Edit UI moves dynamically for any reason:
		this.ticketEditService.combinedEdit$
			.pipe(
				debounceTime(100), //sketchy timing thing to be consistent with the other sketchy timing thing
				this.stashBeforeEditPositionSideEffect((opened) => (opened ? this.updatePrev() : null)),
				filter((etc) => {
					const showFullMenu = this.showFullMenu(etc);
					if (this.showOptions === showFullMenu || !this.dragContainer || etc?.originalList.size > 1) {
						return false;
					}
					return !showFullMenu;
				}),
				switchMap((etc) => this.positionManagerService.positions.get(WindowType.TicketEdit)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((pos) => {
				this.dragContainer?.setX(pos.x + pos.width + this.GAP_BETWEEN_TICKET_EDIT);
				this.dragContainer?.setY(pos.y);
			});

		// hide menu when moving tickets
		planState.tickets.rawEvent$
			.pipe(
				filter((event) => event.type === TicketActionTypes.ticketChangedToDrag),
				withLatestFrom(this.hideMenu$),
				filter((combined) => !combined[1]),
				debounceTime(100),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(() => {
				this.hideMenu$.next(true);
			});

		// show menu after done moving tickets
		planState.tickets.rawEvent$
			.pipe(
				filter(
					(event) =>
						event.type === TicketActionTypes.ticketChangedToDefault ||
						event.type === TicketActionTypes.ticketSavedToFloating
				),
				withLatestFrom(this.hideMenu$),
				filter((combined) => combined[1]),
				debounceTime(100),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(() => {
				this.hideMenu$.next(false);
			});

		this.positionManagerService.positions
			.get(WindowType.TicketEdit)
			.pipe(
				filter(() => !this.showOptions),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((rect: Rectangle) => {
				if (this.dragContainer) {
					this.dragContainer.setX(rect.x + rect.width + this.GAP_BETWEEN_TICKET_EDIT);
					this.dragContainer.setY(rect.y);
				}
			});
	}

	private updatePrev() {
		if (this.dragContainer) {
			this.previousX = this.dragContainer.getX();
			this.previousY = this.dragContainer.getY();
		}
	}

	private showFullMenu(etc: EditTicketsCombined): boolean {
		return etc && etc.originalList.size === 0;
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public duplicate() {
		const selectedList = this.selectedTicketList.getValue();
		const safeTicketList = new Map(this.tickets); //_infinite_ duplicates
		let config: any = { selectImmediately: true };

		if (selectedList === this.planState.floatingTickets) {
			config = { selectImmediately: false };
		}
		//these are "ready" for floating tickets, but the floating ticket code won't currently hit here
		this.planState.actions.clearSelection(safeTicketList, selectedList);
		this.planState.actions.duplicateTicket(safeTicketList, config, selectedList);

		analyticsEventsService.multiselectDuplicate(safeTicketList.size, this.planState.planId);
		safeTicketList.forEach((ticket) =>
			analyticsEventsService.duplicatedATicket(ticket.$id, this.planState.planId, this.syncIsFloat)
		);
	}

	public delete() {
		const selectedList = this.selectedTicketList.getValue();
		this.validatedPlanActions.tryDeleteTickets(this.tickets, {
			disableConfirmation: false,
			ticketListOverride: selectedList,
		});
		analyticsEventsService.multiselectDelete(this.tickets.size, this.planState.planId);
	}

	public editAll() {
		const selectedList = this.selectedTicketList.getValue();
		if (this.anyCompleted$.getValue()) {
			Logging.warning(strings.WARNING_CANNOT_MULTI_EDIT_COMPLETED);
			return;
		}
		this.planState.actions.changeTicketViewToEdit(this.tickets, selectedList);
		this.tickets.forEach((ticket) => {
			analyticsEventsService.viewedATicket(
				ticket.$id,
				this.planState.planId,
				this.planAccessCheck.hasRestrictedAccessSync,
				selectedList === this.planState.floatingTickets
			);
		});
	}

	public editLocations() {
		const selectedList = this.selectedTicketList.getValue();
		this.planState.actions.changeTicketViewToLocationsEdit(this.tickets, selectedList);

		this.dialog.open(TicketEditLocationsComponent, {
			data: {
				data: this.tickets,
				isFloatingTicket$: this.isFloat$,
			},
			autoFocus: true,
			minWidth: '40vw',
		});
	}

	public copyToClipboard() {
		if (clipboardManager.hasActiveAndPullTickets(this.tickets)) {
			Logging.warning(strings.WARNING_CANNOT_COPY_ACTIVE_AND_PULL_TO_CLIPBOARD);
			return;
		}
		clipboardManager.copyTickets();
	}

	public alignRows() {
		if (this.syncIsFloat) {
			return;
		}
		const oldTickets = this.planState.legacyActions.getOldTicketsFromNew(this.tickets);
		this.planState.scopeSoup.alignRows(oldTickets);
	}

	public get syncIsFloat() {
		return this.selectedTicketList.getValue() === this.planState.floatingTickets;
	}

	private positionMenu() {
		if (!this.dragContainer.hasBeenDragged) {
			const rect: Rectangle = calcBoundingRectFromTickets(this.tickets);
			this.dragContainer.centerVertically(
				this.planState.canvasConnector.camera.realToScreenY(rect.y),
				this.planState.canvasConnector.camera.realToScreenY(rect.y + rect.height)
			);
			this.dragContainer.snapToRightEdge();
		}
	}

	/**
	 * This method exists to monitor an etc observable for a specific property changing with specific timing, _without_ requiring a new observable
	 * and without being force to modify all existing observables. When it changes, it's emitted as a callback via a side effect.
	 *
	 * This is mainly to avoid having to rewrite the two editTicketsCombined subscriptions to ensure stable timing.
	 */
	private stashBeforeEditPositionSideEffect(
		editChangedCb: (editOpened: boolean) => void
	): MonoTypeOperatorFunction<EditTicketsCombined> {
		return function stashBeforeEditPositionSideEffectImpl(source) {
			//keep this observable seperate for it's special metadata (need to emit the decorated data so that pairwise has it)
			const speciallyDoctoredOb$ = source.pipe(
				startWith(null as EditTicketsCombined),
				map((etc) => ({ etc, isOpen: !!(etc?.originalList.size > 0) })),
				pairwise(),
				filter((comb) => !!comb[0]),
				tap(([prev, curr]) => {
					//if changed trigger the callback
					if (prev.isOpen !== curr.isOpen) {
						editChangedCb(curr.isOpen);
					}
				})
			);

			//then return one that removes all the metadata
			return speciallyDoctoredOb$.pipe(map((comb) => comb[1].etc));
		};
	}
}
